import { inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
  ActionState,
  LocalStorageService,
  SessionStorageService
} from '@ui/legacy-lib';

import { PropertyMatchBean, PropertyType } from '@ui/shared/model';
import { storageKeys } from '@ui/tenant-pool/shared/config/util';
import { UserStore } from '@ui/tenant-pool/shared/user/data-access';
import {
  CheckApplicationExist,
  CreateApplicationFail,
  CreateApplicationSuccess,
  LoadPropertyMatchBeanQuestions,
  OpenCompleteGarageModal,
  OpenPostLoginModal
} from '../../+state/property-matches/property-matches.actions';
import {
  getCheckApplicationExistActionState,
  getCheckApplicationExistResult,
  getPropertyMatchDataActionState,
  getPropertyMatchDataById,
  getPropertyMatchesQuestions,
  getPropertyMatchQuestionsActionState
} from '../../+state/property-matches/property-matches.selector';
import { OpenCustomQuestionsModal } from '../../+state/custom-questions/custom-questions.actions';

type LoadedApplication = PropertyMatchBean & { id: string };

// The bean is only usable once its own load finished and it is actually in the
// store. Both are still unset right after the check succeeded: the reducer resets
// propertyMatchDataActionState when CheckApplicationExist starts, and the effect
// only dispatches LoadPropertyMatchBean once the check came back.
const isLoadedApplication = ([state, propertyMatch]: [
  ActionState,
  LoadedApplication
]) => !state.pending && state.done && !!propertyMatch?.id;

/**
 * Finishes an "apply to property" intent that was parked in the session storage
 * (`storageKeys.propertyIdToApply`) before the user was authenticated - typically
 * when someone opens a property link, then registers or logs in.
 *
 * Once a user is present it asks the backend whether an application for that
 * property already exists and continues along one of four paths:
 *
 * - **Nothing parked** - clears the key and lets the navigation through.
 * - **Application exists** - waits until its propertyMatchBean is loaded, clears
 *   the parked apply keys and opens the custom questions modal (bean has
 *   unanswered questions) or the complete-garage modal (garage without
 *   questions).
 * - **No application yet** - the `CheckApplicationExist` effect dispatches
 *   `CreateApplication`, which owns the rest of the flow including the redirect.
 *   The navigation is held until that apply settled: it can move the PS out of
 *   the internal tenant pool, and the routes below here are branched on that.
 * - **Check failed** - nothing is dispatched, so the navigation passes.
 *
 * Side effects beyond those modals: dispatches `OpenPostLoginModal` on every run
 * and `CheckApplicationExist` once a user is known.
 *
 * A failed check does not block the navigation - only an unexpected stream error
 * resolves to `false`.
 *
 * @returns Observable emitting a single value: `true` to allow the navigation,
 * `false` only if the stream errored.
 */
export const ApplicationCreationGuard = () => {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const sessionStorage = inject(SessionStorageService);
  const localStorageService = inject(LocalStorageService);

  // TODO: Not sure if this is the best place to open the modal
  store.dispatch(OpenPostLoginModal());

  // We save the ID as a string, but the sessionStorage converts it to a number.
  // This is just a temporary fix, until sessionStorage is fixed
  const propertyIdToApply = sessionStorage.getItem<number | string>(
    storageKeys.propertyIdToApply
  );
  const propertyId = propertyIdToApply ? String(propertyIdToApply) : undefined;

  if (!propertyId || propertyId === 'undefined') {
    sessionStorage.removeItem(storageKeys.propertyIdToApply);
    return of(true);
  }

  const clearApplyStorageKeys = () => {
    sessionStorage.removeItem(storageKeys.propertyIdToApply);
  };

  const waitForApplication = (id: string) =>
    combineLatest([
      store.select(getPropertyMatchDataActionState),
      store.select(getPropertyMatchDataById(id))
    ]).pipe(
      filter(isLoadedApplication),
      take(1),
      map(([, application]) => application)
    );

  const openCustomQuestionsModal = (
    application: LoadedApplication,
    isGarage: boolean
  ) => {
    // If you apply via a property link, then you get redirected to the expose page
    // Before that happens this guard is called, where the custom question modal can be shown
    // The same applies for the expose page
    // Then the custom question modal will be opened twice in a row, which doesn't make sense
    localStorageService.removeItem(storageKeys.customQuestionApplicationId);

    store.dispatch(LoadPropertyMatchBeanQuestions({ id: application.id }));

    store
      .select(getPropertyMatchQuestionsActionState)
      .pipe(
        filter(({ pending, done }) => !pending && done),
        switchMap(() =>
          store.select(getPropertyMatchesQuestions).pipe(take(1))
        ),
        take(1)
      )
      .subscribe(questionContainer => {
        // Use the application captured above instead of re-selecting it by id:
        // loading the questions does not touch propertyMatchesData, but other
        // actions (e.g. a list reload replacing the array) can remove the entry
        // while the questions request is in flight.
        store.dispatch(
          new OpenCustomQuestionsModal(
            { ...application, questionContainer },
            isGarage
          )
        );
      });
  };

  /**
   * The user is read synchronously: this runs under `AuthGuard`, which does
   * not resolve until `fetchUserData` has settled the load.
   */
  if (!inject(UserStore).userData()?.id) return of(true);

  store.dispatch(CheckApplicationExist({ propertyId }));

  return combineLatest([
    store.select(getCheckApplicationExistActionState),
    store.select(getCheckApplicationExistResult)
  ]).pipe(
    // The result is reset when CheckApplicationExist starts, so a result for
    // this property can only be the answer to the check dispatched above.
    filter(
      ([{ pending, error }, result]) =>
        !pending && (!!error || result?.propertyId === propertyId)
    ),
    take(1),
    switchMap(([{ error }, result]) => {
      // A failed check dispatches nothing, so there is nothing to wait for and
      // waiting would stall the navigation for good.
      if (error) return of(undefined);

      // Without an existing application no propertyMatchBean is loaded: the effect
      // dispatches CreateApplication instead, which owns the rest of the flow
      // including the redirect. Hold the navigation until that apply settled -
      // letting it through renders `landlords` / `my-landlord` off the pre-apply
      // pool flag. createApplication$ is exhaustMap'd, so a concurrent guard run
      // waits on the same in-flight apply instead of restarting it.
      if (!result?.applicationId) {
        return actions$.pipe(
          ofType(CreateApplicationSuccess, CreateApplicationFail),
          take(1),
          map(() => undefined)
        );
      }

      return waitForApplication(result.applicationId);
    }),
    tap(application => {
      if (!application) return;

      clearApplyStorageKeys();

      // property is optional on PropertyMatchBean
      const isGarage = application.property?.type === PropertyType.GARAGE;

      if (application.hasQuestions && application.hasUnansweredQuestions) {
        openCustomQuestionsModal(application, isGarage);
      } else if (isGarage) {
        // The modal should only open when the PS applies for a garage,
        // which has no custom questions assigned
        store.dispatch(OpenCompleteGarageModal());
      }
    }),
    map(() => true),
    catchError(() => of(false))
  );
};
